import React, {useEffect, useState} from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Tabs,
    Tab,
    TextField,
    Button,
    MenuItem,
    Grid, Box, Divider,
} from "@mui/material";
import {api} from "../../../services/Main/Api";

const pixTypes = [
    { id: 0, label: "CPF" },
    { id: 1, label: "CNPJ" },
    { id: 2, label: "E-mail" },
    { id: 3, label: "Celular" },
    { id: 4, label: "Chave Aleatória" },
];

const accountTypes = [
    { id: 0, label: "Conta Corrente" },
    { id: 1, label: "Conta Poupança" },
];

export default function RentRecipientModalForm({ open, onClose, onSubmit, cpfCnpj, email, phone}) {
    const [activeTab, setActiveTab] = useState(0);

    // PIX
    const [pixKey, setPixKey] = useState("");
    const [pixType, setPixType] = useState(0); // Start with 0 as default

    // Bank Account
    const [agency, setAgency] = useState("");
    const [accountNumber, setAccountNumber] = useState("");
    const [accountDigit, setAccountDigit] = useState("");
    const [accountType, setAccountType] = useState(0);
    const [selectedBank, setSelectedBank] = useState("");
    const [banks, setBanks] = useState([]);
    const [isLoadingBanks, setIsLoadingBanks] = useState(false);

    // Determine if cpfCnpj is CPF or CNPJ based on length
    const isCpf = cpfCnpj && cpfCnpj.replace(/\D/g, '').length <= 11;
    const isCnpj = cpfCnpj && cpfCnpj.replace(/\D/g, '').length > 11;

    // Filter PIX types based on available data
    const filteredPixTypes = [
        isCpf && { id: 0, label: "CPF" },
        isCnpj && { id: 1, label: "CNPJ" },
        email && { id: 2, label: "E-mail" },
        phone && { id: 3, label: "Celular" },
        // { id: 4, label: "Chave Aleatória" }, // Always available
    ].filter(Boolean);

    const handleTabChange = (e, newValue) => setActiveTab(newValue);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (activeTab === 0) {
            onSubmit({
                type: "pix",
                pixKey,
                pixType,
            });
        } else {
            onSubmit({
                type: "ted",
                agency,
                accountNumber,
                accountDigit,
                accountType,
                bank: selectedBank,
            });
        }
        // Clear fields and close
        setPixKey("");
        setPixType(0);
        setAgency("");
        setAccountNumber("");
        setAccountDigit("");
        setAccountType(0);
        setSelectedBank("");
        onClose();
    };

    const getBanks = () => {
        setIsLoadingBanks(true);
        api.get('fitbank/bank-choices/')
            .then((res) => {
                const {data} = res;
                setBanks(data);
            })
            .catch(error => {
                console.log(error);
            })
            .finally(() => {
                setIsLoadingBanks(false);
            });
    };

    // Fill pixKey based on pixType
    const fillPixKeyBasedOnType = (type) => {
        switch (type) {
            case 0: // CPF
                return isCpf ? cpfCnpj : "";
            case 1: // CNPJ
                return isCnpj ? cpfCnpj : "";
            case 2: // E-mail
                return email || "";
            case 3: // Celular
                return phone || "";
            case 4: // Chave Aleatória
                return ""; // Clear for random key
            default:
                return "";
        }
    };

    // Handle pixType change
    const handlePixTypeChange = (e) => {
        const newPixType = Number(e.target.value);
        setPixType(newPixType);
        setPixKey(fillPixKeyBasedOnType(newPixType));
    };

    // Load banks when component mounts
    useEffect(() => {
        getBanks();
    }, []);

    // Initialize when modal opens
    useEffect(() => {
        if (open && filteredPixTypes.length > 0) {
            // Find the first available type
            const firstType = filteredPixTypes[0].id;
            setPixType(firstType);
            setPixKey(fillPixKeyBasedOnType(firstType));
        }
    }, [open]); // Only run when modal opens

    // Reset form when modal closes
    useEffect(() => {
        if (!open) {
            setPixKey("");
            setPixType(0);
            setAgency("");
            setAccountNumber("");
            setAccountDigit("");
            setAccountType(0);
            setSelectedBank("");
        }
    }, [open]);

    return (
        <Dialog open={open} fullWidth={true} maxWidth={'sm'} onClose={onClose}>
            <DialogTitle>
                {activeTab === 0 ? "Adicionar Chave PIX" : "Adicionar Conta Bancária"}
            </DialogTitle>
            <Divider />
            <DialogContent>
                <Tabs value={activeTab} onChange={handleTabChange} centered>
                    <Tab label="Chave PIX" />
                    <Tab label="Conta Bancária" />
                </Tabs>
                <Box component="form" mt={2} onSubmit={handleSubmit}>
                    {activeTab === 0 ? (
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    select
                                    label="Tipo da Chave"
                                    fullWidth
                                    value={pixType}
                                    onChange={handlePixTypeChange}
                                    required
                                >
                                    {filteredPixTypes.map(opt => (
                                        <MenuItem key={opt.id} value={opt.id}>{opt.label}</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label="Chave"
                                    fullWidth
                                    value={pixKey}
                                    onChange={e => setPixKey(e.target.value)}
                                    required
                                    disabled={pixType !== 4} // Only enable editing for random key
                                />
                            </Grid>
                        </Grid>
                    ) : (
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    label="Agência"
                                    fullWidth
                                    value={agency}
                                    onChange={e => setAgency(e.target.value)}
                                    required
                                    helperText="Infome sem o digito"
                                />
                            </Grid>
                            <Grid item xs={8}>
                                <TextField
                                    label="Conta"
                                    fullWidth
                                    value={accountNumber}
                                    onChange={e => setAccountNumber(e.target.value)}
                                    required
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextField
                                    label="Dígito da conta"
                                    fullWidth
                                    value={accountDigit}
                                    onChange={e => setAccountDigit(e.target.value)}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    select
                                    label="Tipo de Conta"
                                    fullWidth
                                    value={accountType}
                                    onChange={e => setAccountType(Number(e.target.value))}
                                    required
                                >
                                    {accountTypes.map((type) => (
                                        <MenuItem key={type.id} value={type.id}>
                                            {type.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    select
                                    label="Banco"
                                    fullWidth
                                    value={selectedBank}
                                    onChange={e => setSelectedBank(e.target.value)}
                                    required
                                    disabled={isLoadingBanks}
                                    helperText={isLoadingBanks ? "Carregando bancos..." : ""}
                                >
                                    {banks.map((bank) => (
                                        <MenuItem key={bank.id || bank.code} value={bank.id || bank.code}>
                                            {bank.name || bank.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                        </Grid>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancelar</Button>
                <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                >
                    Salvar
                </Button>
            </DialogActions>
        </Dialog>
    );
}